// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {getRemindPresetMenuItems, REMIND_CLEAR_ITEM_ID} from '@app/features/messaging/utils/ReminderMenuUtils';
import {REMIND_ME_DESCRIPTOR} from '@app/features/messaging/utils/ReminderTimePresets';
import {SubMenu as MenuItemSubmenu} from '@app/features/ui/action_menu/ContextMenu';
import {MenuGroup} from '@app/features/ui/action_menu/MenuGroup';
import {MenuItem} from '@app/features/ui/action_menu/MenuItem';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';

interface MessageMenuItemProps {
	message: Message;
	onClose: () => void;
}

const RemindMessagePresetItems: React.FC<MessageMenuItemProps> = observer(({message, onClose}) => {
	const {i18n} = useLingui();
	const items = getRemindPresetMenuItems(message, i18n, {onSelect: onClose});
	const clearItem = items.find((item) => item.id === REMIND_CLEAR_ITEM_ID);
	const presetItems = items.filter((item) => item.id !== REMIND_CLEAR_ITEM_ID);
	return (
		<>
			{clearItem != null && (
				<MenuGroup data-flx="ui.action-menu.items.remind-message-menu-items.clear-group">
					<MenuItem
						onClick={clearItem.onClick}
						danger
						data-flx="ui.action-menu.items.remind-message-menu-items.menu-item.clear"
					>
						{clearItem.label}
					</MenuItem>
				</MenuGroup>
			)}
			<MenuGroup data-flx="ui.action-menu.items.remind-message-menu-items.menu-group">
				{presetItems.map((item) => (
					<MenuItem
						key={item.id}
						onClick={item.onClick}
						danger={item.danger}
						data-flx="ui.action-menu.items.remind-message-menu-items.menu-item.preset"
					>
						{item.label}
					</MenuItem>
				))}
			</MenuGroup>
		</>
	);
});

export const RemindMessageMenuItem: React.FC<MessageMenuItemProps> = observer(({message, onClose}) => {
	const {i18n} = useLingui();
	return (
		<MenuItemSubmenu
			label={i18n._(REMIND_ME_DESCRIPTOR)}
			render={() => (
				<RemindMessagePresetItems
					message={message}
					onClose={onClose}
					data-flx="ui.action-menu.items.remind-message-menu-items.remind-message-menu-item.remind-message-preset-items"
				/>
			)}
			data-flx="ui.action-menu.items.remind-message-menu-items.menu-item-submenu"
		/>
	);
});
